/**
 * Derived metrics: degree, hub/bridge/orphan flags, narrative weight, zone
 * centroid placeholder.
 *
 * Read-only helpers. Never mutate the inputs. See docs/DECISIONS.md ADR-009.
 *
 * Hub ranking uses an "effective weight" that is the entity's `importance`
 * field if present (range 0..1), otherwise its normalized degree. This means:
 *   - Most entities don't set `importance` and rank by topology alone.
 *   - A curator can elevate or demote a specific node without rewriting the
 *     graph by setting `importance: 0.92` (or similar).
 */
import type { Counter, Move, Position, Zone } from '../types/domain';

export type CentralityMetrics = {
  degree: Map<string, number>;
  inDegree: Map<string, number>;
  outDegree: Map<string, number>;
  bridgeScore: Map<string, number>;
  isHub: Map<string, boolean>;
  isBridge: Map<string, boolean>;
  isOrphan: Map<string, boolean>;
  popularityRank: Map<string, number>;
  /**
   * "Effective weight" in [0, 1]: importance override if set, otherwise
   * degree normalized to the dataset's max. Used to drive radial shell
   * placement and hub thresholds.
   */
  weight: Map<string, number>;
};

export function computeCentrality(
  positions: Position[],
  moves: Move[],
  counters: Counter[],
): CentralityMetrics {
  const inDegree = new Map<string, number>();
  const outDegree = new Map<string, number>();
  const neighborsByPos = new Map<string, Set<string>>();
  const zoneOfPos = new Map(positions.map((p) => [p.id, p.zone]));

  for (const p of positions) {
    inDegree.set(p.id, 0);
    outDegree.set(p.id, 0);
    neighborsByPos.set(p.id, new Set());
  }

  const addEdge = (a: string, b: string) => {
    outDegree.set(a, (outDegree.get(a) ?? 0) + 1);
    inDegree.set(b, (inDegree.get(b) ?? 0) + 1);
    neighborsByPos.get(a)?.add(b);
    neighborsByPos.get(b)?.add(a);
  };

  for (const m of moves) {
    addEdge(m.from, m.to);
  }
  for (const c of counters) {
    const move = moves.find((m) => m.id === c.intercepts);
    if (move) addEdge(move.from, c.result);
  }

  const degree = new Map<string, number>();
  for (const p of positions) {
    degree.set(p.id, (inDegree.get(p.id) ?? 0) + (outDegree.get(p.id) ?? 0));
  }

  // Effective weight: importance override if present, else normalized degree.
  const maxDeg = Math.max(1, ...[...degree.values()]);
  const importanceOverride = new Map<string, number>();
  for (const p of positions) {
    if (typeof p.importance === 'number') importanceOverride.set(p.id, p.importance);
  }
  const weight = new Map<string, number>();
  for (const p of positions) {
    const override = importanceOverride.get(p.id);
    if (override !== undefined) {
      weight.set(p.id, Math.max(0, Math.min(1, override)));
    } else {
      weight.set(p.id, (degree.get(p.id) ?? 0) / maxDeg);
    }
  }

  const bridgeScore = new Map<string, number>();
  for (const p of positions) {
    const zs = new Set<Zone>();
    for (const n of neighborsByPos.get(p.id) ?? []) {
      const z = zoneOfPos.get(n);
      if (z) zs.add(z);
    }
    bridgeScore.set(p.id, zs.size);
  }

  // Hub threshold uses effective weight, not raw degree.
  const sortedWeights = [...weight.values()].sort((a, b) => a - b);
  const wQ3 = sortedWeights[Math.floor(sortedWeights.length * 0.75)] ?? 0;
  const isHub = new Map<string, boolean>();
  for (const [id, w] of weight) isHub.set(id, w >= wQ3 && w > 0.05);

  const isBridge = new Map<string, boolean>();
  for (const [id, s] of bridgeScore) isBridge.set(id, s >= 2);

  const isOrphan = new Map<string, boolean>();
  // Truly disconnected only. Degree-1 nodes used to be folded in
  // here, which the renderer then treated as "orphan-styled"
  // (shrunk + dimmed) — making them indistinguishable from real
  // orphans. Now the visual distinction tracks the real topology.
  for (const [id, d] of degree) isOrphan.set(id, d === 0);

  // popularityRank by from-zone
  const popularityRank = new Map<string, number>();
  const byZone = new Map<Zone, Move[]>();
  for (const m of moves) {
    const z = zoneOfPos.get(m.from);
    if (!z) continue;
    if (!byZone.has(z)) byZone.set(z, []);
    byZone.get(z)!.push(m);
  }
  for (const [, list] of byZone) {
    const sorted = [...list].sort((a, b) => (b.popularity ?? 0.5) - (a.popularity ?? 0.5));
    sorted.forEach((m, i) => {
      popularityRank.set(m.id, 1 - i / Math.max(1, sorted.length - 1));
    });
  }

  return {
    degree,
    inDegree,
    outDegree,
    bridgeScore,
    isHub,
    isBridge,
    isOrphan,
    popularityRank,
    weight,
  };
}
